// AYA Chauffeur — reproduction fonctionnelle de la planche (10 écrans, carte réelle).
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Menu, Bell, X, ChevronLeft, ChevronRight, Star, Phone, MessageSquare, MapPin, Clock, Navigation,
  Check, CheckCircle2, ShieldCheck, Home, ListChecks, Wallet, User, Coins, FileText, BarChart3,
  Settings, LifeBuoy, Share2, Route, Siren, Timer, ArrowUpRight, ArrowDownLeft,
} from "lucide-react";
import FlagCi from "@/components/FlagCi";
import AyaMenu from "@/components/AyaMenu";
import ChauffeurMap from "./ChauffeurMap";
import "./chauffeur.css";
import { ChauffeurFiscalCard } from "./ChauffeurFiscalCard";

const IMG = "/aya/chauffeur";
const DRIVER_POS = [5.3448, -3.9905];
const PICKUP = [5.3565, -3.9772];
const DEST = [5.3247, -4.0223];
const BASE_PRICE = 4000;
const fmt = (n) => `${n.toLocaleString("fr-FR")} FCFA`;

const HISTORY = [
  { id: "h1", from: "Cocody", to: "Plateau", meta: "10:27 · 4,8 km · 15 min", price: 4000 },
  { id: "h2", from: "Riviera 3", to: "Marcory", meta: "09:15 · 7,2 km · 22 min", price: 5000 },
  { id: "h3", from: "Angré", to: "Plateau", meta: "08:42 · 6,1 km · 18 min", price: 4500 },
  { id: "h4", from: "Cocody", to: "Bingerville", meta: "07:20 · 12,4 km · 28 min", price: 7000 },
];

const WALLET_KEY = "aya_chauffeur_wallet_v1";
const WITHDRAW_METHODS = [
  { id: "orange", name: "Orange Money", bg: "#F97316", text: "OM" },
  { id: "wave", name: "Wave", bg: "#2563EB", text: "wave" },
  { id: "mtn", name: "MTN Money", bg: "#FACC15", text: "MTN", dark: true },
];
const defaultWallet = () => ({
  balance: 28500,
  tx: HISTORY.map((h) => ({ id: h.id, kind: "credit", label: `Course ${h.from} → ${h.to}`, meta: `Aujourd'hui · ${h.meta.split(" · ")[0]}`, amount: h.price })),
});
const readWallet = () => {
  try { const w = JSON.parse(localStorage.getItem(WALLET_KEY)); if (w && Number.isFinite(w.balance)) return w; } catch { /* vide */ }
  return defaultWallet();
};
const writeWallet = (w) => { try { localStorage.setItem(WALLET_KEY, JSON.stringify(w)); } catch { /* plein */ } };

const ClientRow = ({ actions }) => (
  <div className="chf-row">
    <img src={`${IMG}/chf_client.jpg`} alt="Cédric D." className="chf-avatar" />
    <span style={{ flex: 1, minWidth: 0 }}>
      <span style={{ display: "block", fontSize: 13, fontWeight: 800, color: "#fff" }}>Cédric D.</span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10.5, color: "#CFE5D6", fontWeight: 700 }}>
        <Star size={11} color="#F5A623" fill="#F5A623" /> 4,9 <span style={{ color: "#9DB4A6" }}>(24 courses)</span>
      </span>
    </span>
    {actions}
  </div>
);

const Stats = () => (
  <div className="chf-stats" data-testid="chf-stats">
    <div><div className="v">12</div><div className="l">Courses aujourd'hui</div></div>
    <div><div className="v">28 500<span style={{ fontSize: 9, fontWeight: 800 }}> FCFA</span></div><div className="l">Revenus</div></div>
    <div><div className="v" style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><Star size={13} color="#F5A623" fill="#F5A623" /> 4,8</div><div className="l">Note moyenne</div></div>
  </div>
);

export default function ChauffeurPage() {
  const navigate = useNavigate();
  const [screen, setScreen] = useState("login");
  const [menuOpen, setMenuOpen] = useState(false);
  const [online, setOnline] = useState(true);
  const [countdown, setCountdown] = useState(15);
  const [proposal, setProposal] = useState(BASE_PRICE + 500);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [ridePrice, setRidePrice] = useState(BASE_PRICE);
  const [progress, setProgress] = useState(0);
  const [tab, setTab] = useState("Aujourd'hui");
  const [rated, setRated] = useState(0);
  const [wallet, setWallet] = useState(readWallet);
  const [wdAmount, setWdAmount] = useState("");
  const [wdMethod, setWdMethod] = useState("orange");
  const [wdPhone, setWdPhone] = useState("+225 07 08 09 10 11");
  const [wdSending, setWdSending] = useState(false);
  const requestTimer = useRef(null);

  const updateWallet = (fn) => setWallet((w) => { const nw = fn(w); writeWallet(nw); return nw; });
  const creditRide = (price) => updateWallet((w) => ({
    balance: w.balance + price,
    tx: [{ id: `c-${Date.now()}`, kind: "credit", label: "Course Cocody → Plateau", meta: "À l'instant", amount: price }, ...w.tx].slice(0, 50),
  }));
  const confirmWithdraw = () => {
    const amount = parseInt(wdAmount, 10);
    if (!Number.isFinite(amount) || amount < 1000) return toast.error("Montant minimum : 1 000 FCFA");
    if (amount > wallet.balance) return toast.error("Montant supérieur à votre solde");
    if (!wdPhone.trim()) return toast.error("Indiquez le numéro Mobile Money");
    setWdSending(true);
    setTimeout(() => {
      setWdSending(false);
      const m = WITHDRAW_METHODS.find((x) => x.id === wdMethod);
      updateWallet((w) => ({
        balance: w.balance - amount,
        tx: [{ id: `w-${Date.now()}`, kind: "debit", label: `Retrait ${m.name}`, meta: `À l'instant · ${wdPhone}`, amount }, ...w.tx].slice(0, 50),
      }));
      setWdAmount("");
      toast.success(`Retrait de ${fmt(amount)} envoyé sur ${m.name} ✓`);
      setScreen("wallet");
    }, 1800);
  };

  useEffect(() => () => clearInterval(requestTimer.current), []);
  useEffect(() => {
    if (screen !== "request") return undefined;
    setCountdown(15);
    const t = setInterval(() => setCountdown((c) => {
      if (c <= 1) { clearInterval(t); toast("Demande expirée"); setScreen("home"); return 15; }
      return c - 1;
    }), 1000);
    return () => clearInterval(t);
  }, [screen]);
  useEffect(() => {
    if (screen !== "riding") return undefined;
    const t = setInterval(() => setProgress((p) => Math.min(1, p + 0.02)), 600);
    return () => clearInterval(t);
  }, [screen]);

  const accept = (price) => { setRidePrice(price); setScreen("accepted"); toast.success("Course acceptée ✓"); };
  const sendProposal = () => {
    setSending(true);
    setTimeout(() => { setSending(false); toast.success("Cédric a accepté votre proposition ✓"); accept(proposal); }, 1800);
  };
  const resetRide = () => { setProgress(0); setProposal(BASE_PRICE + 500); setMessage(""); setRated(0); };

  const Nav = ({ active }) => (
    <nav className="chf-nav" data-testid="chf-nav">
      {[
        { id: "home", Icon: Home, label: "Accueil", go: () => setScreen("home") },
        { id: "courses", Icon: ListChecks, label: "Courses", go: () => setScreen("courses") },
        { id: "revenus", Icon: Wallet, label: "Revenus", go: () => setScreen("wallet") },
        { id: "profil", Icon: User, label: "Profil", go: () => setScreen("profil") },
      ].map((t) => (
        <button key={t.id} className={active === t.id ? "on" : ""} data-testid={`chf-nav-${t.id}`} onClick={t.go}>
          <t.Icon size={18} strokeWidth={active === t.id ? 2.4 : 2} />{t.label}
        </button>
      ))}
    </nav>
  );

  /* ── 1. CONNEXION / ACCUEIL ── */
  if (screen === "login") {
    return (
      <div className="chf-login" data-testid="chauffeur-login">
        <img src={`${IMG}/chf_hero.jpg`} alt="AYA Chauffeur" className="bg" />
        <div className="ov" />
        <div className="content">
          <div style={{ fontSize: 33, fontWeight: 900, color: "#fff", lineHeight: 1 }}>AYA</div>
          <div style={{ fontSize: 26, fontWeight: 900, color: "#2FCF62", lineHeight: 1.1 }}>Chauffeur <FlagCi size={20} style={{ verticalAlign: "-2px" }} /></div>
          <p style={{ margin: "8px 0 12px", fontSize: 13, fontWeight: 700, color: "#fff", lineHeight: 1.35 }}>Plus de courses, plus de revenus<br />en toute sécurité.</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
            {["Demandes de course à proximité", "Paiements sécurisés", "Navigation intégrée", "Support 24h/24"].map((l) => (
              <span key={l} style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 11.5, fontWeight: 600, color: "#E8F6EC" }}>
                <span style={{ width: 20, height: 20, borderRadius: 6, background: "#10B981", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Check size={12} color="#fff" strokeWidth={3} /></span>{l}
              </span>
            ))}
          </div>
          <button className="chf-btn" data-testid="chf-login-btn" onClick={() => setScreen("home")}>Se connecter</button>
          <div style={{ height: 9 }} />
          <button className="chf-btn ghost" data-testid="chf-signup-btn" onClick={() => { toast.success("Compte chauffeur de démonstration créé ✓"); setScreen("home"); }}>Créer un compte</button>
        </div>
      </div>
    );
  }

  /* ── 8. FIN DE COURSE ── */
  if (screen === "done") {
    return (
      <div className="chf-page" data-testid="chauffeur-done" style={{ background: "#06281A", position: "relative", overflow: "hidden" }}>
        {["#F5A623", "#10B981", "#60A5FA", "#F87171", "#FBBF24", "#34D399"].map((c, i) => (
          <span key={i} className="chf-confetti" style={{ background: c, left: `${8 + i * 15}%`, top: 30, animationDelay: `${i * 0.35}s` }} />
        ))}
        <div className="chf-header" style={{ background: "transparent" }}>
          <span style={{ width: 34 }} />
          <h1> </h1>
          <button className="chf-hbtn" data-testid="chf-done-close" onClick={() => { resetRide(); setScreen("home"); }}><X size={17} /></button>
        </div>
        <div className="chf-container" style={{ justifyContent: "center", paddingBottom: 30 }}>
          <div style={{ textAlign: "center" }}>
            <span style={{ width: 76, height: 76, borderRadius: "50%", background: "linear-gradient(135deg,#10B981,#0E7A4F)", display: "inline-flex", alignItems: "center", justifyContent: "center", boxShadow: "0 6px 22px rgba(16,185,129,0.45)" }}><Check size={38} color="#fff" strokeWidth={3} /></span>
            <h2 style={{ margin: "14px 0 0", fontSize: 20, fontWeight: 900, color: "#fff", lineHeight: 1.25 }}>Course terminée<br />avec succès !</h2>
          </div>
          <div className="chf-card" style={{ marginTop: 18 }} data-testid="chf-done-amount">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 12.5, fontWeight: 800, color: "#fff" }}>Montant de la course</span>
              <span style={{ fontSize: 16, fontWeight: 900, color: "#34D399" }}>{fmt(ridePrice)}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 8, fontSize: 10.5, color: "#CFE5D6", fontWeight: 600 }}>
              <CheckCircle2 size={15} color="#34D399" /> Paiement reçu par AYA sur votre portefeuille chauffeur.
            </div>
          </div>
          <div className="chf-card">
            <ClientRow actions={
              rated ? (
                <span style={{ display: "inline-flex", gap: 1 }}>{Array.from({ length: rated }).map((_, i) => <Star key={i} size={13} color="#F5A623" fill="#F5A623" />)}</span>
              ) : (
                <button className="chf-chip" data-testid="chf-rate-client" onClick={() => { setRated(5); toast.success("Merci pour votre note ✓"); }}>Noter le client</button>
              )
            } />
          </div>
          <div style={{ height: 16 }} />
          <button className="chf-btn" data-testid="chf-done-home" onClick={() => { resetRide(); setScreen("home"); }}>Retour à l'accueil</button>
          <div style={{ height: 9 }} />
          <button className="chf-btn ghost" data-testid="chf-done-revenus" onClick={() => { resetRide(); setScreen("wallet"); }}>Voir mes revenus</button>
        </div>
      </div>
    );
  }

  return (
    <div className="chf-page" data-testid="chauffeur-page">
      <AyaMenu open={menuOpen} onClose={() => setMenuOpen(false)} />

      {/* ── HEADERS ── */}
      {screen === "home" && (
        <div className="chf-header">
          <button className="chf-hbtn" data-testid="chf-menu-btn" onClick={() => setMenuOpen(true)}><Menu size={17} /></button>
          <h1><span style={{ color: "#fff" }}>AYA</span> <span style={{ color: "#2FCF62" }}>Chauffeur</span></h1>
          <button className="chf-hbtn" style={{ border: "none", background: "none" }} data-testid="chf-bell-btn" onClick={() => toast("Aucune nouvelle notification")}><Bell size={18} /></button>
        </div>
      )}
      {screen === "request" && (
        <div className="chf-header">
          <span style={{ width: 34 }} />
          <h1 style={{ textAlign: "left", paddingLeft: 2 }}>Nouvelle demande</h1>
          <span className="chf-badge-km">à 1,2 km</span>
          <button className="chf-hbtn" style={{ border: "none", background: "none" }} data-testid="chf-request-close" onClick={() => setScreen("home")}><X size={18} /></button>
        </div>
      )}
      {screen === "negotiate" && (
        <div className="chf-header">
          <button className="chf-hbtn" style={{ border: "none", background: "none" }} data-testid="chf-nego-back" onClick={() => setScreen("request")}><ChevronLeft size={20} /></button>
          <h1>Proposer un prix</h1>
          <button className="chf-hbtn" style={{ border: "none", background: "none" }} data-testid="chf-nego-close" onClick={() => setScreen("home")}><X size={18} /></button>
        </div>
      )}
      {["accepted", "onboard"].includes(screen) && (
        <div className="chf-header">
          <button className="chf-hbtn" style={{ border: "none", background: "none" }} onClick={() => setScreen(screen === "accepted" ? "home" : "accepted")} data-testid="chf-step-back"><ChevronLeft size={20} /></button>
          <h1>{screen === "accepted" ? "Course acceptée" : "Client à bord"}</h1>
          <span style={{ width: 34 }} />
        </div>
      )}
      {screen === "riding" && (
        <div className="chf-header">
          <button className="chf-hbtn" style={{ border: "none", background: "none" }} onClick={() => setScreen("onboard")} data-testid="chf-riding-back"><ChevronLeft size={20} /></button>
          <h1>Course en cours<span className="sub">Navigation active</span></h1>
          <span style={{ width: 34 }} />
        </div>
      )}
      {["courses", "profil", "wallet", "withdraw"].includes(screen) && (
        <div className="chf-header">
          <button className="chf-hbtn" style={{ border: "none", background: "none" }} onClick={() => setScreen(screen === "withdraw" ? "wallet" : "home")} data-testid="chf-page-back"><ChevronLeft size={20} /></button>
          <h1>{{ courses: "Mes courses", profil: "Mon profil", wallet: "Mon portefeuille", withdraw: "Retirer mes gains" }[screen]}</h1>
          <span style={{ width: 34 }} />
        </div>
      )}

      <div className="chf-container" style={{ paddingBottom: 14 }}>

        {/* ── 2. EN LIGNE / HORS LIGNE ── */}
        {screen === "home" && (
          <>
            <ChauffeurMap radar={online} center={[5.336, -4.012]} zoom={12} height={252} testid="chf-home-map" />
            <div className="chf-card" data-testid="chf-online-card">
              <div className="chf-row">
                <span style={{ width: 26, height: 26, borderRadius: "50%", background: online ? "#10B981" : "#24382C", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Check size={14} color="#fff" strokeWidth={3} /></span>
                <span style={{ flex: 1 }}>
                  <span style={{ display: "block", fontSize: 13, fontWeight: 800, color: "#fff" }}>{online ? "Vous êtes en ligne" : "Vous êtes hors ligne"}</span>
                  <span style={{ fontSize: 9.5, color: "#9DB4A6", fontWeight: 600 }}>{online ? "Vous recevez les demandes autour de vous" : "Activez pour recevoir des demandes"}</span>
                </span>
                <button className={`chf-switch ${online ? "on" : ""}`} data-testid="chf-online-toggle" aria-label="En ligne" onClick={() => setOnline(!online)} />
              </div>
            </div>
            <Stats />
            <button className="chf-prow" data-testid="chf-requests-row" disabled={!online} style={{ opacity: online ? 1 : 0.5 }} onClick={() => setScreen("request")}>
              <h4>Demandes à proximité</h4>
              <span style={{ minWidth: 20, height: 20, borderRadius: 999, background: "#10B981", color: "#fff", fontSize: 11, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "0 6px" }}>2</span>
              <ChevronRight size={16} color="#9DB4A6" />
            </button>
          </>
        )}

        {/* ── 3. NOUVELLE DEMANDE ── */}
        {screen === "request" && (
          <>
            <ChauffeurMap from={PICKUP} to={DEST} color="#10B981" height={170} testid="chf-request-map" />
            <div className="chf-card">
              <div className="chf-row" style={{ alignItems: "flex-start" }}>
                <MapPin size={15} color="#F87171" style={{ marginTop: 2, flexShrink: 0 }} />
                <span style={{ flex: 1 }}>
                  <span style={{ display: "block", fontSize: 13, fontWeight: 800, color: "#fff", lineHeight: 1.3 }}>Cocody (Riviera 3)<br />→ Plateau (Tour A)</span>
                  <span style={{ fontSize: 10.5, color: "#9DB4A6", fontWeight: 700 }}>4,8 km · ~ 15 min</span>
                </span>
              </div>
              <div className="chf-row" style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(16,185,129,0.18)" }}>
                <Coins size={17} color="#F5A623" style={{ flexShrink: 0 }} />
                <span style={{ flex: 1 }}>
                  <span style={{ display: "block", fontSize: 10, color: "#9DB4A6", fontWeight: 700 }}>Prix proposé par le client</span>
                  <span style={{ fontSize: 16.5, fontWeight: 900, color: "#fff" }} data-testid="chf-client-price">{fmt(BASE_PRICE)}</span>
                </span>
              </div>
            </div>
            <div className="chf-card"><ClientRow /></div>
            <div style={{ display: "flex", gap: 8, marginTop: 9 }}>
              {[
                { Icon: MapPin, v: "1,2 km", l: "à vous" },
                { Icon: Timer, v: "~ 3 min", l: "arrivée client" },
                { Icon: Clock, v: "Trajet", l: "15 min" },
              ].map((t) => (
                <div key={t.l} className="chf-tile"><t.Icon size={13} color="#34D399" /><div className="v">{t.v}</div><div className="l">{t.l}</div></div>
              ))}
            </div>
            <div style={{ height: 12 }} />
            <button className="chf-btn" data-testid="chf-accept-btn" onClick={() => accept(BASE_PRICE)}>
              <span style={{ flex: 1, textAlign: "center" }}>Accepter<br /><span style={{ fontSize: 11, fontWeight: 700, opacity: 0.9 }}>{fmt(BASE_PRICE)}</span></span>
              <span style={{ width: 30, height: 30, borderRadius: "50%", background: "rgba(255,255,255,0.22)", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 900 }} data-testid="chf-countdown">{countdown}</span>
            </button>
            <div style={{ height: 9 }} />
            <button className="chf-btn ghost" data-testid="chf-propose-btn" onClick={() => setScreen("negotiate")}>Proposer un autre prix</button>
          </>
        )}

        {/* ── 4. PROPOSER UN PRIX ── */}
        {screen === "negotiate" && (
          <>
            <ChauffeurMap from={PICKUP} to={DEST} color="#10B981" height={150} testid="chf-nego-map" />
            <div className="chf-card">
              <div className="chf-row">
                <Coins size={17} color="#F5A623" style={{ flexShrink: 0 }} />
                <span style={{ flex: 1 }}>
                  <span style={{ display: "block", fontSize: 10, color: "#9DB4A6", fontWeight: 700 }}>Prix proposé par le client</span>
                  <span style={{ fontSize: 16.5, fontWeight: 900, color: "#fff" }}>{fmt(BASE_PRICE)}</span>
                </span>
              </div>
            </div>
            <div style={{ margin: "12px 0 6px", fontSize: 13, fontWeight: 800, color: "#fff" }}>Votre proposition</div>
            <div className="chf-stepper" data-testid="chf-proposal-stepper">
              <button data-testid="chf-proposal-minus" onClick={() => setProposal((p) => Math.max(BASE_PRICE - 1000, p - 500))}>−</button>
              <span className="v" data-testid="chf-proposal-value">{fmt(proposal)}</span>
              <button data-testid="chf-proposal-plus" onClick={() => setProposal((p) => Math.min(BASE_PRICE + 5000, p + 500))}>+</button>
            </div>
            <div style={{ textAlign: "center", marginTop: 7, fontSize: 10.5, fontWeight: 800, color: proposal >= BASE_PRICE ? "#34D399" : "#F87171" }} data-testid="chf-proposal-delta">
              {proposal === BASE_PRICE ? "Identique au prix proposé" : `${proposal > BASE_PRICE ? "+ " : "− "}${fmt(Math.abs(proposal - BASE_PRICE)).replace(" FCFA", " FCFA")} par rapport au prix proposé`}
            </div>
            <div style={{ height: 12 }} />
            <input className="chf-input" placeholder="Message (optionnel)" value={message} onChange={(e) => setMessage(e.target.value)} data-testid="chf-message-input" />
            <div style={{ display: "flex", gap: 7, marginTop: 9, flexWrap: "wrap" }}>
              {["Je suis en route", "Trafic important", "À bientôt"].map((s) => (
                <button key={s} className="chf-chip" data-testid={`chf-suggest-${s.split(" ")[0]}`} onClick={() => setMessage(s)}>{s}</button>
              ))}
            </div>
            <div style={{ height: 14 }} />
            <button className="chf-btn" data-testid="chf-send-proposal" disabled={sending} onClick={sendProposal}>{sending ? "Envoi de la proposition…" : "Envoyer ma proposition"}</button>
          </>
        )}

        {/* ── 5. COURSE ACCEPTÉE ── */}
        {screen === "accepted" && (
          <>
            <div className="chf-card" style={{ marginTop: 4 }}>
              <div className="chf-row">
                <MapPin size={16} color="#60A5FA" style={{ flexShrink: 0 }} />
                <span style={{ flex: 1 }}>
                  <span style={{ display: "block", fontSize: 12, fontWeight: 800, color: "#fff" }}>Rendez-vous au point de prise en charge</span>
                  <span style={{ fontSize: 10.5, color: "#9DB4A6", fontWeight: 700 }}>1,2 km · 3 min</span>
                </span>
                <button className="chf-btn blue" style={{ width: "auto", height: 33, padding: "0 12px", fontSize: 10.5, borderRadius: 999 }} data-testid="chf-navigation-btn"
                  onClick={() => window.open(`https://www.google.com/maps/dir/${DRIVER_POS[0]},${DRIVER_POS[1]}/${PICKUP[0]},${PICKUP[1]}`, "_blank")}>
                  <Navigation size={12} /> Navigation
                </button>
              </div>
            </div>
            <ChauffeurMap from={DRIVER_POS} to={PICKUP} color="#2563EB" showCar carAt={DRIVER_POS} height={185} testid="chf-accepted-map" />
            <div className="chf-card">
              <ClientRow actions={
                <span style={{ display: "inline-flex", gap: 7 }}>
                  <button className="chf-iconbtn" data-testid="chf-call-btn" aria-label="Téléphone" onClick={() => window.open("tel:+2250701020304")}><Phone size={15} /></button>
                  <button className="chf-iconbtn" data-testid="chf-msg-btn" aria-label="Message" onClick={() => navigate("/aya-prive?ctx=" + encodeURIComponent("Message au client Cédric D. : je suis en route vers Riviera 3."))}><MessageSquare size={15} /></button>
                </span>
              } />
            </div>
            <div className="chf-card">
              <div className="chf-line">
                <MapPin size={15} color="#60A5FA" style={{ marginTop: 1, flexShrink: 0 }} />
                <span style={{ flex: 1 }}><h4>Point de prise en charge</h4><h4 style={{ fontWeight: 700 }}>Riviera 3</h4><p>(Pharmacie St Viateur)</p></span>
                <button className="chf-chip" data-testid="chf-pickup-details" onClick={() => toast("Repère : face à la pharmacie, côté station.")}>Détails</button>
              </div>
              <div className="chf-line">
                <MapPin size={15} color="#F87171" style={{ marginTop: 1, flexShrink: 0 }} />
                <span><h4 style={{ fontWeight: 700, color: "#9DB4A6", fontSize: 10.5 }}>Destination</h4><h4>Plateau (Tour A)</h4></span>
              </div>
            </div>
            <div style={{ height: 10 }} />
            <button className="chf-btn" data-testid="chf-arrived-btn" onClick={() => setScreen("onboard")}>J'y suis arrivé</button>
          </>
        )}

        {/* ── 6. CLIENT À BORD ── */}
        {screen === "onboard" && (
          <>
            <div className="chf-card" style={{ marginTop: 4 }}>
              <ClientRow actions={
                <span style={{ display: "inline-flex", gap: 7 }}>
                  <button className="chf-iconbtn" aria-label="Téléphone" onClick={() => window.open("tel:+2250701020304")}><Phone size={15} /></button>
                  <button className="chf-iconbtn" aria-label="Message" onClick={() => navigate("/aya-prive?ctx=" + encodeURIComponent("Message au client Cédric D."))}><MessageSquare size={15} /></button>
                </span>
              } />
            </div>
            <ChauffeurMap from={PICKUP} to={DEST} color="#2563EB" showCar carAt={PICKUP} height={235} testid="chf-onboard-map" />
            <div className="chf-card">
              <div className="chf-row">
                <MapPin size={16} color="#F87171" style={{ flexShrink: 0 }} />
                <span style={{ flex: 1 }}>
                  <span style={{ display: "block", fontSize: 10, color: "#9DB4A6", fontWeight: 700 }}>Destination</span>
                  <span style={{ fontSize: 13.5, fontWeight: 800, color: "#fff" }}>Plateau (Tour A)</span>
                </span>
                <span style={{ textAlign: "right", fontSize: 11, fontWeight: 800, color: "#CFE5D6" }}>4,8 km<br /><span style={{ color: "#9DB4A6", fontWeight: 700 }}>~ 15 min</span></span>
              </div>
            </div>
            <div style={{ height: 10 }} />
            <button className="chf-btn" data-testid="chf-start-ride" onClick={() => { setProgress(0); setScreen("riding"); }}>Démarrer la course</button>
          </>
        )}

        {/* ── 7. COURSE EN COURS ── */}
        {screen === "riding" && (
          <>
            <div style={{ position: "relative", marginTop: 4 }}>
              <ChauffeurMap from={PICKUP} to={DEST} color="#2563EB" showCar progress={progress} height={268} testid="chf-riding-map" />
              <div className="chf-float" data-testid="chf-riding-eta">
                <span className="big">{Math.max(1, Math.round(15 * (1 - progress)))} min</span><br />Arrivée estimée <b>10:42</b>
              </div>
            </div>
            <div className="chf-card">
              <div style={{ display: "flex" }}>
                <span style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
                  <Clock size={15} color="#34D399" />
                  <span><b style={{ fontSize: 13.5, color: "#fff" }} data-testid="chf-riding-km">{(4.8 * (1 - progress)).toFixed(1).replace(".", ",")} km</b><br /><span style={{ fontSize: 9.5, color: "#9DB4A6", fontWeight: 700 }}>Distance restante</span></span>
                </span>
                <span style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
                  <Clock size={15} color="#34D399" />
                  <span><b style={{ fontSize: 13.5, color: "#fff" }}>10:42</b><br /><span style={{ fontSize: 9.5, color: "#9DB4A6", fontWeight: 700 }}>Arrivée estimée</span></span>
                </span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 9 }}>
              {[
                { Icon: Share2, l: "Partager", act: () => { navigator.clipboard?.writeText("Course AYA en cours — arrivée 10:42 Plateau (Tour A)"); toast.success("Trajet copié ✓"); } },
                { Icon: Route, l: "Itinéraire", act: () => window.open(`https://www.google.com/maps/dir/${PICKUP[0]},${PICKUP[1]}/${DEST[0]},${DEST[1]}`, "_blank") },
                { Icon: Siren, l: "Assistance", danger: true, act: () => navigate("/aya-prive?ctx=" + encodeURIComponent("Assistance chauffeur AYA : j'ai besoin d'aide pendant ma course.")) },
              ].map((a) => (
                <button key={a.l} className="chf-tile" style={{ cursor: "pointer", border: a.danger ? "1px solid rgba(220,38,38,0.55)" : undefined }} data-testid={`chf-action-${a.l}`} onClick={a.act}>
                  <a.Icon size={15} color={a.danger ? "#F87171" : "#34D399"} /><div className="l" style={{ color: a.danger ? "#F87171" : undefined, fontSize: 9.5 }}>{a.l}</div>
                </button>
              ))}
            </div>
            <div style={{ height: 12 }} />
            <button className="chf-btn danger" data-testid="chf-end-ride" onClick={() => { creditRide(ridePrice); setScreen("done"); }}>Terminer la course</button>
          </>
        )}

        {/* ── 9. MES COURSES ── */}
        {screen === "courses" && (
          <>
            <div className="chf-tabs" data-testid="chf-courses-tabs">
              {["Aujourd'hui", "Cette semaine", "Ce mois"].map((t) => (
                <button key={t} className={`chf-tab ${tab === t ? "on" : ""}`} data-testid={`chf-tab-${t.split(" ")[0]}`} onClick={() => setTab(t)}>{t}</button>
              ))}
            </div>
            <Stats />
            <div style={{ height: 4 }} />
            {HISTORY.map((h) => (
              <div key={h.id} className="chf-card" style={{ marginTop: 8 }} data-testid={`chf-course-${h.id}`}>
                <div className="chf-row">
                  <MapPin size={15} color="#34D399" style={{ flexShrink: 0 }} />
                  <span style={{ flex: 1 }}>
                    <span style={{ display: "block", fontSize: 12.5, fontWeight: 800, color: "#fff" }}>{h.from} → {h.to}</span>
                    <span style={{ fontSize: 10, color: "#9DB4A6", fontWeight: 700 }}>{h.meta}</span>
                  </span>
                  <span style={{ textAlign: "right" }}>
                    <span style={{ display: "block", fontSize: 12.5, fontWeight: 900, color: "#fff" }}>{fmt(h.price)}</span>
                    <span style={{ fontSize: 9.5, fontWeight: 800, color: "#34D399" }}>Payé</span>
                  </span>
                </div>
              </div>
            ))}
          </>
        )}

        {/* ── PORTEFEUILLE ── */}
        {screen === "wallet" && (
          <>
            <div className="chf-card" style={{ background: "linear-gradient(140deg,#0E7A4F,#06281A)", border: "1px solid rgba(52,211,153,0.55)", padding: "16px 14px" }} data-testid="chf-wallet-card">
              <div className="chf-row">
                <Wallet size={20} color="#A7F3D0" />
                <span style={{ fontSize: 11, fontWeight: 700, color: "#BBE7CF" }}>Solde disponible</span>
              </div>
              <div style={{ fontSize: 28, fontWeight: 900, color: "#fff", marginTop: 6 }} data-testid="chf-wallet-balance">{fmt(wallet.balance)}</div>
              <div style={{ fontSize: 9.5, color: "#BBE7CF", fontWeight: 600, marginTop: 2 }}>Paiements des courses reçus par AYA</div>
            </div>
            <div style={{ height: 10 }} />
            <button className="chf-btn" data-testid="chf-withdraw-btn" onClick={() => setScreen("withdraw")}><ArrowDownLeft size={16} /> Retirer mes gains</button>
            <div style={{ margin: "16px 0 4px", fontSize: 13.5, fontWeight: 800, color: "#fff" }}>Historique</div>
            {wallet.tx.map((t) => (
              <div key={t.id} className="chf-card" style={{ marginTop: 8 }} data-testid={`chf-tx-${t.id}`}>
                <div className="chf-row">
                  <span style={{ width: 30, height: 30, borderRadius: "50%", background: t.kind === "credit" ? "rgba(16,185,129,0.15)" : "rgba(220,38,38,0.12)", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {t.kind === "credit" ? <ArrowUpRight size={14} color="#34D399" /> : <ArrowDownLeft size={14} color="#F87171" />}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 12, fontWeight: 800, color: "#fff" }}>{t.label}</span>
                    <span style={{ fontSize: 9.5, color: "#9DB4A6", fontWeight: 700 }}>{t.meta}</span>
                  </span>
                  <b style={{ fontSize: 12.5, color: t.kind === "credit" ? "#34D399" : "#F87171" }}>{t.kind === "credit" ? "+" : "−"} {fmt(t.amount)}</b>
                </div>
              </div>
            ))}
          </>
        )}

        {/* ── RETRAIT MOBILE MONEY ── */}
        {screen === "withdraw" && (
          <>
            <div className="chf-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#9DB4A6" }}>Solde disponible</span>
              <b style={{ fontSize: 14.5, color: "#34D399" }}>{fmt(wallet.balance)}</b>
            </div>
            <div style={{ margin: "12px 0 6px", fontSize: 12.5, fontWeight: 800, color: "#fff" }}>Montant du retrait</div>
            <input className="chf-input" type="number" inputMode="numeric" placeholder="Ex : 10 000" value={wdAmount}
              onChange={(e) => setWdAmount(e.target.value)} data-testid="chf-wd-amount" style={{ fontSize: 16, fontWeight: 800 }} />
            <div style={{ display: "flex", gap: 7, marginTop: 8 }}>
              {[5000, 10000].map((v) => (
                <button key={v} className="chf-chip" data-testid={`chf-wd-quick-${v}`} onClick={() => setWdAmount(String(Math.min(v, wallet.balance)))}>{fmt(v)}</button>
              ))}
              <button className="chf-chip" data-testid="chf-wd-quick-all" onClick={() => setWdAmount(String(wallet.balance))}>Tout retirer</button>
            </div>
            <div style={{ margin: "14px 0 6px", fontSize: 12.5, fontWeight: 800, color: "#fff" }}>Recevoir sur</div>
            <div style={{ display: "flex", gap: 7 }}>
              {WITHDRAW_METHODS.map((m) => (
                <button key={m.id} data-testid={`chf-wd-method-${m.id}`} onClick={() => setWdMethod(m.id)}
                  style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 5, padding: "10px 4px", borderRadius: 13, cursor: "pointer", background: wdMethod === m.id ? "rgba(16,185,129,0.14)" : "#0A2418", border: wdMethod === m.id ? "1.5px solid #34D399" : "1px solid rgba(16,185,129,0.28)", color: "#ECF5EE", fontSize: 9, fontWeight: 800 }}>
                  <span style={{ width: 36, height: 24, borderRadius: 6, background: m.bg, color: m.dark ? "#1F2937" : "#fff", fontSize: 8, fontWeight: 900, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{m.text}</span>
                  {m.name}
                </button>
              ))}
            </div>
            <div style={{ margin: "12px 0 6px", fontSize: 12.5, fontWeight: 800, color: "#fff" }}>Numéro Mobile Money</div>
            <input className="chf-input" value={wdPhone} onChange={(e) => setWdPhone(e.target.value)} data-testid="chf-wd-phone" placeholder="+225 ..." />
            <div style={{ height: 16 }} />
            <button className="chf-btn" data-testid="chf-wd-confirm" disabled={wdSending} onClick={confirmWithdraw}>
              {wdSending ? "Retrait en cours…" : `Confirmer le retrait${wdAmount ? ` · ${fmt(parseInt(wdAmount, 10) || 0)}` : ""}`}
            </button>
            <div style={{ textAlign: "center", marginTop: 9, fontSize: 9.5, color: "#9DB4A6", fontWeight: 600 }}>Retrait généralement reçu en moins de 2 minutes.</div>
          </>
        )}

        {/* ── 10. MON PROFIL ── */}
        {screen === "profil" && (
          <>
            <div style={{ textAlign: "center", padding: "10px 0 4px" }}>
              <img src={`${IMG}/chf_driver.jpg`} alt="Jean K." style={{ width: 82, height: 82, borderRadius: "50%", objectFit: "cover", border: "3px solid rgba(16,185,129,0.6)" }} />
              <h2 style={{ margin: "8px 0 0", fontSize: 17, fontWeight: 900, color: "#fff" }}>Jean K.</h2>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#CFE5D6", marginTop: 2 }}><Star size={11} color="#F5A623" fill="#F5A623" style={{ verticalAlign: -1 }} /> 4,8 <span style={{ color: "#9DB4A6" }}>(128 courses)</span></div>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 6, padding: "4px 11px", borderRadius: 999, background: "rgba(16,185,129,0.15)", border: "1px solid rgba(16,185,129,0.5)", color: "#34D399", fontSize: 10, fontWeight: 800 }}>
                <ShieldCheck size={12} /> Chauffeur vérifié
              </span>
            </div>
            <div className="chf-card" data-testid="chf-vehicle-card">
              <div className="chf-row">
                <img src="/aya/transport/tp_chauffeur.jpg" alt="Toyota Corolla" style={{ width: 52, height: 38, borderRadius: 9, objectFit: "cover", flexShrink: 0 }} />
                <span style={{ flex: 1 }}>
                  <span style={{ display: "block", fontSize: 12.5, fontWeight: 800, color: "#fff" }}>Toyota Corolla</span>
                  <span style={{ fontSize: 10, color: "#9DB4A6", fontWeight: 700 }}>AB 1234 CI · Noir</span>
                </span>
                <button className="chf-chip" data-testid="chf-vehicle-edit" onClick={() => toast("Modification du véhicule bientôt disponible")}>Modifier</button>
              </div>
            </div>
            {[
              { Icon: FileText, l: "Mes documents", sub: "Permis, carte grise, assurance", badge: "Validés", act: () => toast.success("Tous vos documents sont validés ✓") },
              { Icon: Wallet, l: "Mon portefeuille", sub: fmt(wallet.balance), act: () => setScreen("wallet") },
              { Icon: BarChart3, l: "Mes statistiques", act: () => setScreen("courses") },
              { Icon: Settings, l: "Paramètres", act: () => toast("Paramètres bientôt disponibles") },
              { Icon: LifeBuoy, l: "Support", act: () => navigate("/aya-prive?ctx=" + encodeURIComponent("Support chauffeur AYA : j'ai besoin d'aide.")) },
            ].map((r) => (
              <button key={r.l} className="chf-prow" data-testid={`chf-profil-${r.l.split(" ")[1] || r.l}`} onClick={r.act}>
                <r.Icon size={16} color="#34D399" style={{ flexShrink: 0 }} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <h4>{r.l}</h4>
                  {r.sub && <span style={{ fontSize: 9.5, color: "#9DB4A6", fontWeight: 700 }}>{r.sub}</span>}
                </span>
                {r.badge && <span style={{ padding: "3px 9px", borderRadius: 999, background: "rgba(16,185,129,0.15)", border: "1px solid rgba(16,185,129,0.5)", color: "#34D399", fontSize: 9, fontWeight: 800 }}>{r.badge} ✓</span>}
                <ChevronRight size={15} color="#9DB4A6" />
              </button>
            ))}
            <ChauffeurFiscalCard />
          </>
        )}
      </div>

      {["home", "courses", "profil", "wallet"].includes(screen) && (
        <Nav active={screen === "home" ? "home" : screen === "courses" ? "courses" : screen === "wallet" ? "revenus" : "profil"} />
      )}
    </div>
  );
}
